import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {useToggle} from "react-use";
import {css} from "@emotion/css";
import {Button, H2, Text, styled} from "@reside/ui";
import {RoleNameEnum} from "../../utils/types/common";
import {AppState, select} from "../../store";
import {DocumentTitle} from "../../atoms/document-title";
import {AdminRoles} from "../../atoms/admin-roles";
import {BackdropSpinner} from "../../atoms/spinner";
import {PagePagination} from "../../atoms/pagination";
import {ReportingLayout} from "./reporting-layout";
import {ListTableUserPermissions} from "./atoms/list-table-permissions";
import {usePaginatedUserPermissionsQuery} from "./atoms/permission-management-modal/hooks/usePaginatedUserPermissionsQuery";
import {useBulkRemoveUserPermissions} from "./atoms/permission-management-modal/hooks/useBulkUserPermissionsActions";
import {PERMISSIONS_MANAGEMENT_BULK_PATH} from "./atoms/permissions-bulk-edit";
import {
  DEFAULT_PERMISSIONS_MANAGEMENT_FILTERS,
  PermissionsManagementToolbar,
} from "./atoms/permissions-management-filters";
import {RemoveAllPermissionsConfirmationModal} from "./atoms/permissions-management-filters/RemoveAllPermissionsConfirmationModal";
import {pinCurrentUserFirst} from "./atoms/permissions-user-detail";

type Props = {
  currentUserId?: string;
  roles: RoleNameEnum[];
  isResideAdmin: boolean;
};

const PageReportingPermissionsManagementRenderer = ({
  roles,
  isResideAdmin,
  currentUserId,
}: Props) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(
    DEFAULT_PERMISSIONS_MANAGEMENT_FILTERS,
  );
  const [selectedUserIds, setSelectedUserIds] = useState<ReadonlyArray<string>>(
    [],
  );
  const [isRemoveAllPermissionsModalOpen, toggleRemoveAllPermissionsModal] =
    useToggle(false);

  const {
    page,
    setPage,
    setSort,
    query: {refetch, isLoading, isFetching, error, data},
  } = usePaginatedUserPermissionsQuery({roles, enabled: true, filters});

  const {mutate: removeAllPermissions, isLoading: isRemovingPermissions} =
    useBulkRemoveUserPermissions();

  const users = useMemo(
    () => pinCurrentUserFirst(data?.content ?? [], currentUserId),
    [currentUserId, data?.content],
  );

  useEffect(() => {
    setSelectedUserIds([]);
    toggleRemoveAllPermissionsModal(false);
  }, [
    page,
    filters.searchTerm,
    filters.roleFilters,
    filters.permissionsFilters,
  ]);

  const handleBulkEditPermissions = () => {
    if (selectedUserIds.length === 0) {
      return;
    }

    const selectedUsers = users.filter(
      user => user.id && selectedUserIds.includes(user.id),
    );

    navigate(PERMISSIONS_MANAGEMENT_BULK_PATH, {
      state: {users: selectedUsers},
    });
  };

  const handleRemoveAllPermissionsClick = () => {
    if (selectedUserIds.length === 0) {
      return;
    }

    toggleRemoveAllPermissionsModal(true);
  };

  const handleRemoveAllPermissionsConfirm = () => {
    const selectedUsers = users.filter(
      user => user.id && selectedUserIds.includes(user.id),
    );

    removeAllPermissions(selectedUsers, {
      onSuccess: () => {
        setSelectedUserIds([]);
      },
      onSettled: () => {
        toggleRemoveAllPermissionsModal(false);
      },
    });
  };

  return (
    <ReportingLayout isActionRowVisible={false}>
      <DocumentTitle title="Reside - Admin - Permissions Management" />
      <AdminRoles
        roles={[RoleNameEnum.RESIDE_ADMIN, RoleNameEnum.ORGANIZATION_ADMIN]}
      >
        <PageContainer>
          <Text fontWeight="bold" className={styles.title}>
            Permissions Management
          </Text>

          {error ? (
            <div className={styles.error}>
              <H2>Error while fetching users</H2>
              <Button color="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : (
            <>
              <PermissionsManagementToolbar
                availableRoles={roles}
                filters={filters}
                onFiltersChange={setFilters}
                selectedUserCount={selectedUserIds.length}
                isRemovingPermissions={isRemovingPermissions}
                onBulkEditPermissions={handleBulkEditPermissions}
                onRemoveAllPermissions={handleRemoveAllPermissionsClick}
                pagination={
                  data && data.totalElements > 0 ? (
                    <PagePagination
                      current={page + 1}
                      pageSize={data.size}
                      total={data.totalElements}
                      onChange={current => setPage(current - 1)}
                    />
                  ) : null
                }
              />
              <BackdropSpinner active={isLoading || isFetching}>
                {data && (
                  <ListTableUserPermissions
                    users={users}
                    selectedUserIds={selectedUserIds}
                    onSelectedUserIdsChange={setSelectedUserIds}
                    includeOtherGroup={isResideAdmin}
                    onRowClick={(_, row) =>
                      navigate(
                        `/admin/permissions/management/${row.rowData.id}`,
                        {
                          state: {user: row.rowData},
                        },
                      )
                    }
                    onSort={(properties, direction) =>
                      setSort({properties, direction})
                    }
                  />
                )}
              </BackdropSpinner>
              <RemoveAllPermissionsConfirmationModal
                isOpen={isRemoveAllPermissionsModalOpen}
                selectedUserCount={selectedUserIds.length}
                isRemovingPermissions={isRemovingPermissions}
                onRequestClose={() => toggleRemoveAllPermissionsModal(false)}
                onConfirm={handleRemoveAllPermissionsConfirm}
              />
            </>
          )}
        </PageContainer>
      </AdminRoles>
    </ReportingLayout>
  );
};

export const PageReportingPermissionsManagement = () => {
  const currentUserId = useSelector(
    (state: AppState) => select.adminSession.user(state)?.id,
  );
  const roles = useSelector((state: AppState) =>
    select.adminSession.visibleRoles(state),
  );
  const isResideAdmin = useSelector((state: AppState) =>
    select.adminSession.isResideAdmin(state),
  );

  return (
    <PageReportingPermissionsManagementRenderer
      currentUserId={currentUserId}
      roles={roles}
      isResideAdmin={isResideAdmin}
    />
  );
};

const PageContainer = styled.div`
  padding: 8px 0;
`;

const styles = {
  title: css`
    font-size: 24px;
    line-height: 32px;
    margin: 0 0 24px;
  `,
  error: css`
    text-align: center;
    padding: 100px;

    button {
      margin: 40px auto;
    }
  `,
};
